import { Button, Icon, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@elit-hair/elit-ui";
import { createFileRoute } from "@tanstack/react-router";
import { DateTime } from "luxon";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { TbExternalLink, TbPlus } from "react-icons/tb";

import { Link } from "@/components/link";
import { useStrapiPages } from "@/queries/strapi/page.queries";
import { APP_ROUTES_FULL, APP_ROUTES_TO } from "@/utils/routes";

import { AddCustomPageDialog } from "./components/add-custom-page-dialog";
import { ConnectToClaudeButton } from "./components/connect-to-claude-button";
import { CopyAiPromptButton } from "./components/copy-ai-prompt-button";
import { CustomPageCard } from "./components/custom-page-card";

export const Route = createFileRoute(APP_ROUTES_FULL.MANAGEMENT_CUSTOM_PAGES)({
    component: ManagementCustomPages,
    staticData: {
        config: {
            breadcrumb: {
                title: "custom_pages",
                to: APP_ROUTES_TO.MANAGEMENT_CUSTOM_PAGES,
            },
            title: "custom_pages",
        },
    },
});

function ManagementCustomPages() {
    const { t } = useTranslation();
    const [addDialogOpen, setAddDialogOpen] = useState(false);

    const { data: pages = [], isLoading } = useStrapiPages();

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="space-y-1">
                    <h2 className="text-2xl font-bold tracking-tight">{t("custom_pages")}</h2>
                    <p className="text-sm text-muted-foreground">{t("custom_pages_description")}</p>
                </div>

                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <ConnectToClaudeButton />
                    <CopyAiPromptButton />
                    <Button type="button" onClick={() => setAddDialogOpen(true)}>
                        <Icon icon={TbPlus} className="mr-2" />
                        {t("add_custom_page")}
                    </Button>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto">
                <div className="hidden lg:block">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>{t("name")}</TableHead>
                                <TableHead>{t("slug")}</TableHead>
                                <TableHead>{t("description")}</TableHead>
                                <TableHead>{t("updated_at")}</TableHead>
                                <TableHead>{t("actions")}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell className="text-center">{t("loading")}</TableCell>
                                </TableRow>
                            ) : pages.length === 0 ? (
                                <TableRow>
                                    <TableCell className="text-center">{t("no_custom_pages_found")}</TableCell>
                                </TableRow>
                            ) : (
                                pages.map((page) => (
                                    <TableRow key={page.documentId}>
                                        <TableCell className="font-medium">{page.name}</TableCell>
                                        <TableCell>
                                            <code className="text-xs">{page.slug}</code>
                                        </TableCell>
                                        <TableCell className="max-w-md truncate">{page.description}</TableCell>
                                        <TableCell>
                                            {page.updatedAt
                                                ? DateTime.fromISO(page.updatedAt).toFormat("dd.MM.yyyy HH:mm")
                                                : "-"}
                                        </TableCell>
                                        <TableCell>
                                            <Button type="button" variant="ghost" size="sm" asChild>
                                                <Link to={APP_ROUTES_TO.CUSTOM_PAGE} params={{ slug: page.slug }}>
                                                    <Icon icon={TbExternalLink} className="mr-2" />
                                                    {t("open")}
                                                </Link>
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>

                <div className="space-y-3 lg:hidden">
                    {isLoading ? (
                        <p className="py-8 text-center text-muted-foreground">{t("loading")}</p>
                    ) : pages.length === 0 ? (
                        <p className="py-8 text-center text-muted-foreground">{t("no_custom_pages_found")}</p>
                    ) : (
                        pages.map((page) => <CustomPageCard key={page.documentId} page={page} />)
                    )}
                </div>
            </div>

            <AddCustomPageDialog open={addDialogOpen} onOpenChange={setAddDialogOpen} />
        </div>
    );
}
